import { Anchor, Box, Image, Text, Title, useMantineTheme } from "@mantine/core";
import { useHover, useMediaQuery } from "@mantine/hooks";
import { useState } from "react";
import { Tools } from "../generated/generated-operations";
import { useTranslation } from "../hooks/translation-hook";
import { texts } from "../texts/texts";

const IMAGE_BASE = "https://promaster-publish.panasonic.divid.se/files/f83bb8f8-030a-4df2-ba4e-5ad57aa41ff1/blob_";

function buildImageUrl(blobId: string): string {
  return `${IMAGE_BASE}${blobId}`;
}

type Props = {
  readonly carouselImages?: ReadonlyArray<string>;
  readonly showTakeALookAction?: boolean;
  readonly isCarouselOpen?: boolean;
  readonly onToggleCarousel?: () => void;
  readonly tool: Tools;
};

export function ToolCard({ carouselImages, showTakeALookAction, isCarouselOpen = false, onToggleCarousel, tool }: Props): JSX.Element {
  const translate = useTranslation();
  const theme = useMantineTheme();
  const { hovered, ref } = useHover<HTMLDivElement>();
  const [focusedWithin, setFocusedWithin] = useState(false);
  const isCoarsePointer = useMediaQuery("(hover: none), (pointer: coarse)");

  const toolName = tool.name ?? "Toolkit tool";
  const translatedDescription = translate(tool.description_short, "toolkit");
  const hasDescription = translatedDescription.trim().length > 0;
  const hasUrl = typeof tool.url === "string" && tool.url.trim().length > 0;
  const fallbackToolColor = theme.colors[theme.primaryColor]?.[7] ?? "#2f3437";
  const toolColor = tool.primaryColor ?? fallbackToolColor;

  const resolvedCarouselImages = carouselImages ?? [];
  const defaultTakeALookVisibility = resolvedCarouselImages.length > 0;

  const hasTakeALook = Boolean(onToggleCarousel) && (showTakeALookAction ?? defaultTakeALookVisibility);
  const hasActions = hasTakeALook || hasUrl;
  const hasRevealContent = hasDescription || hasActions;
  const isActive = hovered || focusedWithin || isCoarsePointer;

  function handleBlur(event: React.FocusEvent<HTMLDivElement>): void {
    const nextFocusedElement = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    setFocusedWithin(nextFocusedElement !== null && event.currentTarget.contains(nextFocusedElement));
  }

  return (
    <>
      <Box
        ref={ref}
        tabIndex={hasRevealContent ? 0 : undefined}
        onFocus={() => setFocusedWithin(true)}
        onBlur={handleBlur}
        style={{
          position: "relative",
          aspectRatio: "16 / 9",
          borderRadius: 8,
          overflow: "hidden",
          cursor: hasActions ? "pointer" : "default",
          boxShadow: isActive ? "0 18px 36px rgba(20, 28, 35, 0.18)" : "0 8px 18px rgba(20, 28, 35, 0.12)",
          outline: focusedWithin ? `2px solid ${toolColor}` : "none",
          outlineOffset: 4,
          transition: "box-shadow 240ms ease, transform 240ms ease",
          transform: isActive ? "translateY(-2px)" : "translateY(0)",
        }}
      >
        {tool.image ? (
          <Image
            src={buildImageUrl(tool.image)}
            alt={toolName}
            fit="cover"
            styles={{
              root: {
                position: "absolute",
                inset: 0,
                transition: "transform 360ms ease, filter 240ms ease",
                transform: isActive ? "scale(1.045)" : "scale(1)",
                filter: isActive ? "saturate(0.96) contrast(0.94)" : "saturate(1) contrast(1)",
              },
            }}
          />
        ) : (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #28323c 0%, #4c5965 100%)",
            }}
          />
        )}

        <Box
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: toolColor,
            opacity: isActive ? 0.82 : 0.18,
            transition: "opacity 240ms ease",
          }}
        />

        <Box
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0, 0, 0, 0.58) 0%, rgba(0, 0, 0, 0.1) 58%, transparent 100%)",
            opacity: isActive ? 0.28 : 1,
            transition: "opacity 220ms ease",
          }}
        />

        <Box
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <Box
            aria-hidden={!isActive}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              minHeight: 0,
              padding: "var(--mantine-spacing-lg)",
              paddingBottom: "var(--mantine-spacing-md)",
              opacity: isActive ? 1 : 0,
              transform: isActive ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 220ms ease, transform 220ms ease",
              pointerEvents: isActive ? "auto" : "none",
            }}
          >
            {hasDescription && (
              <Text
                size="sm"
                c="white"
                mb="md"
                lineClamp={4}
                style={{
                  opacity: 0.94,
                  lineHeight: 1.45,
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
                }}
              >
                {translatedDescription}
              </Text>
            )}
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                flexWrap: "wrap",
                gap: "0.75rem 1.5rem",
                alignItems: "flex-start",
              }}
            >
              {hasTakeALook && (
                <Anchor
                  component="button"
                  type="button"
                  onClick={onToggleCarousel}
                  tabIndex={isActive ? 0 : -1}
                  aria-expanded={isCarouselOpen}
                  c="white"
                  fw={600}
                  size="sm"
                  tt="uppercase"
                  style={{
                    appearance: "none",
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                    letterSpacing: "0.08em",
                    padding: 0,
                    textDecoration: "underline",
                    textUnderlineOffset: 4,
                  }}
                >
                  {translate(texts.take_a_look) || "Take a Look"}
                </Anchor>
              )}
              {hasUrl && (
                <Anchor
                  href={tool.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={isActive ? 0 : -1}
                  c="white"
                  fw={600}
                  size="sm"
                  tt="uppercase"
                  style={{
                    letterSpacing: "0.08em",
                    textDecoration: "underline",
                    textUnderlineOffset: 4,
                  }}
                >
                  {translate(texts.log_in) || "Log In"}
                </Anchor>
              )}
            </Box>
          </Box>

          <Box
            style={{
              backgroundColor: toolColor,
              width: "100%",
              padding: "0.95rem 1.25rem",
              boxShadow: "0 -10px 26px rgba(0, 0, 0, 0.16)",
            }}
          >
            <Title
              order={4}
              c="white"
              ta="center"
              tt={"uppercase"}
            >
              {tool.title ?? toolName}
            </Title>
          </Box>
        </Box>
      </Box>
    </>
  );
}
